import Flickity from 'flickity';

const HINTS_HIDDEN_STORAGE_KEY = 'flickity-hints-hidden';

class FlickitySlider extends HTMLElement {
  constructor() {
    super();

    this.restoreHintsVisibilityFromSession();

    this.slider = this.querySelector('.flickity__slider');
    if (!this.slider) {
      return;
    }

    // Check if this is mobile-only carousel
    this.isMobileOnly = this.hasAttribute('data-flickity-mobile-only');

    if (this.isMobileOnly && !this.isMobileViewport()) {
      return; // Don't initialize on desktop
    }

    const configAttr = this.dataset.flickityConfig || this.slider.dataset.flickityConfig;
    this.options = configAttr ? JSON.parse(configAttr) : {};
    this.hasInitialized = false;

    this.observeForInit();
  }

  restoreHintsVisibilityFromSession() {
    try {
      const areHintsHidden = sessionStorage.getItem(HINTS_HIDDEN_STORAGE_KEY) === 'true';
      if (areHintsHidden) {
        document.documentElement.setAttribute('data-flickity-hints-hidden', 'true');
      }
    } catch (error) {
      // Ignore storage access issues.
    }
  }

  isMobileViewport() {
    const matches = window.matchMedia('(max-width: 749px)').matches;
    return matches;
  }

  observeForInit() {
    if (!('IntersectionObserver' in window)) {
      this.initFlickity();
      return;
    }

    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.initFlickity();
          if (this.io) this.io.disconnect();
        });
      },
      { rootMargin: '600px 0px', threshold: 0 },
    );

    this.io.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();

    if (this.handlePointerDown) {
      this.removeEventListener('pointerdown', this.handlePointerDown);
    }
    if (this.handlePointerMove) {
      this.removeEventListener('pointermove', this.handlePointerMove);
    }
    if (this.handlePointerUp) {
      this.removeEventListener('pointerup', this.handlePointerUp);
      this.removeEventListener('pointercancel', this.handlePointerUp);
    }

    if (this.handleTouchStart) {
      this.removeEventListener('touchstart', this.handleTouchStart);
    }
    if (this.handleTouchMove) {
      this.removeEventListener('touchmove', this.handleTouchMove);
    }
    if (this.handleTouchEnd) {
      this.removeEventListener('touchend', this.handleTouchEnd);
      this.removeEventListener('touchcancel', this.handleTouchEnd);
    }
  }

  initFlickity() {
    if (this.hasInitialized) return;
    this.hasInitialized = true;

    // Enable Flickity's built-in lazyload on mobile
    if (this.isMobileOnly) {
      this.options.lazyLoad = 1; // Load adjacent images
    }

    this.flkty = new Flickity(this.slider, this.options);

    if (this.isMobileOnly) {
      this.cardMediaContainer = this.closest('.card__media--flickity');
      this.cardInner = this.closest('.card__inner');
      this.progressBar = this.cardInner?.querySelector('[data-flickity-progress]');
      this.hintBadge = this.cardInner?.querySelector('.card-media-hint');
      this.cardLink = this.closest('.card-wrapper')?.querySelector('.full-unstyled-link');
      this.lastDragTime = 0;
      this.tapGuard = {
        downAt: 0,
        startX: 0,
        startY: 0,
        moved: false,
        lastDuration: 0,
        lastUpAt: 0,
      };
      this.scrollDetection = {
        startX: 0,
        startY: 0,
        isVerticalScroll: false,
      };

      // Bind scroll detection to prevent swiping during vertical scroll
      this.bindScrollDetection();

      if (this.progressBar) {
        this.flkty.on('settle', () => this.updateProgressBar());
        this.flkty.on('scroll', () => this.updateProgressBar());
        this.updateProgressBar();
      }

      this.flkty.on('dragStart', () => {
        this.lastDragTime = Date.now();
      });
      this.flkty.on('dragEnd', () => {
        this.lastDragTime = Date.now();
      });

      if (this.cardLink) {
        this.bindTapGuards();
        this.flkty.on('staticClick', () => {
          if (this.shouldNavigateOnTap()) {
            window.location.href = this.cardLink.href;
          }
        });
      }
    }

    this.init();
  }

  hideAllSwipeHints() {
    // Mark globally that hints have been hidden - CSS will handle hiding via [data-flickity-hints-hidden]
    document.documentElement.setAttribute('data-flickity-hints-hidden', 'true');

    try {
      sessionStorage.setItem(HINTS_HIDDEN_STORAGE_KEY, 'true');
    } catch (error) {
      // Ignore storage access issues.
    }
  }

  updateProgressBar() {
    if (!this.progressBar) return;

    const slideCount = this.flkty.slides.length;
    const currentIndex = this.flkty.selectedIndex;
    const progress = ((currentIndex + 1) / slideCount) * 100;

    this.progressBar.style.width = progress + '%';

    // Mark as swiped after first interaction and hide all hints globally
    if (currentIndex > 0 && this.cardMediaContainer) {
      this.cardMediaContainer.classList.add('card__media--swiped');
      this.hideAllSwipeHints();
    }
  }

  bindTapGuards() {
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);

    this.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
    this.addEventListener('pointermove', this.handlePointerMove, { passive: true });
    this.addEventListener('pointerup', this.handlePointerUp, { passive: true });
    this.addEventListener('pointercancel', this.handlePointerUp, { passive: true });
  }

  bindScrollDetection() {
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    this.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    this.addEventListener('touchcancel', this.handleTouchEnd, { passive: true });
  }

  handleTouchStart(event) {
    const touch = event.touches[0];
    this.scrollDetection.startX = touch.clientX;
    this.scrollDetection.startY = touch.clientY;
    this.scrollDetection.isVerticalScroll = false;
  }

  handleTouchMove(event) {
    if (!event.touches.length) return;

    const touch = event.touches[0];
    const deltaX = Math.abs(touch.clientX - this.scrollDetection.startX);
    const deltaY = Math.abs(touch.clientY - this.scrollDetection.startY);

    // If movement is detected and not yet determined
    if (!this.scrollDetection.isVerticalScroll && (deltaX > 5 || deltaY > 5)) {
      // Vertical scroll is dominant
      if (deltaY > deltaX * 1.5) {
        this.scrollDetection.isVerticalScroll = true;
        // Disable Flickity dragging
        if (this.flkty) {
          this.flkty.options.draggable = false;
          this.flkty.updateDraggable();
        }
      }
    }
  }

  handleTouchEnd() {
    // Re-enable dragging after touch ends
    if (this.scrollDetection.isVerticalScroll && this.flkty) {
      this.flkty.options.draggable = true;
      this.flkty.updateDraggable();
    }
    this.scrollDetection.isVerticalScroll = false;
  }

  handlePointerDown(event) {
    this.tapGuard.downAt = Date.now();
    this.tapGuard.startX = event.clientX;
    this.tapGuard.startY = event.clientY;
    this.tapGuard.moved = false;
  }

  handlePointerMove(event) {
    if (!this.tapGuard.downAt) return;
    const dx = Math.abs(event.clientX - this.tapGuard.startX);
    const dy = Math.abs(event.clientY - this.tapGuard.startY);
    if (dx > 8 || dy > 8) {
      this.tapGuard.moved = true;
    }
  }

  handlePointerUp() {
    if (!this.tapGuard.downAt) return;
    const now = Date.now();
    this.tapGuard.lastDuration = now - this.tapGuard.downAt;
    this.tapGuard.lastUpAt = now;
    this.tapGuard.downAt = 0;
  }

  shouldNavigateOnTap() {
    const now = Date.now();
    const recentTap = now - this.tapGuard.lastUpAt < 400;
    const longEnough = this.tapGuard.lastDuration >= 60;
    const notMoved = !this.tapGuard.moved;
    const notJustDragged = now - this.lastDragTime > 300;

    return recentTap && longEnough && notMoved && notJustDragged;
  }

  init() {
    if (this.flkty) this.flkty.resize();
  }
}

customElements.define('flickity-slider', FlickitySlider);
window.FlickitySlider = FlickitySlider;
